import type { DataOrigin, LegalCitation, QuizQuestion } from '@buakei/types';
import { Button, DataOriginBadge } from '@buakei/ui';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { CitationLink } from '@/inspector/CitationLink';
import { DEMO_SOURCES } from '@/demo/legal-sources';

/** Map a passage ID to an inspectable citation using the demo source set. */
function citationForPassage(passageId: string): LegalCitation | null {
  for (const source of DEMO_SOURCES) {
    for (const version of source.versions) {
      const passage = version.passages.find((p) => p.id === passageId);
      if (passage) {
        return {
          id: `quiz_${passageId}`,
          sourceId: source.id,
          versionId: version.id,
          passageIds: [passageId],
          label: `${source.title} — ${passage.label}`,
          dataOrigin: source.dataOrigin,
        };
      }
    }
  }
  return null;
}

/** A question as the runner needs it: demo questions cite demo passages, live ones sources. */
export type RunnerQuestion = Omit<QuizQuestion, 'citationIds'> & { citationIds?: string[] };

/**
 * QuizCard flow (M1-06): answer, see sourced feedback, continue, see score. Live quizzes
 * (ADR-021) pass `renderSources` to show their real sources and `resultNote` for the score.
 */
export function QuizRunner<Q extends RunnerQuestion>({
  quiz,
  renderSources,
  resultNote = 'Practice result only. It is not saved in this demo.',
}: {
  quiz: { dataOrigin?: DataOrigin; questions: Q[] };
  renderSources?: (question: Q) => ReactNode;
  resultNote?: string;
}) {
  const [index, setIndex] = useState(0);
  const [choice, setChoice] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const question = quiz.questions[index];

  useEffect(() => {
    if (index > 0 || finished) headingRef.current?.focus();
  }, [index, finished]);

  if (finished || !question) {
    return (
      <section
        aria-labelledby="quiz-result"
        className="flex flex-col gap-3 rounded-lg border border-line-200 bg-white p-6"
      >
        <h2
          id="quiz-result"
          ref={headingRef}
          tabIndex={-1}
          className="font-serif text-2xl font-semibold text-navy-900"
        >
          You scored {score} of {quiz.questions.length}
        </h2>
        <p className="text-muted-600">{resultNote}</p>
        <Button
          className="self-start"
          onClick={() => {
            setIndex(0);
            setScore(0);
            setChoice(null);
            setSubmitted(false);
            setFinished(false);
          }}
        >
          Try again
        </Button>
      </section>
    );
  }

  const correct = choice === question.correctOptionId;
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!choice || submitted) return;
    setSubmitted(true);
    if (correct) setScore((s) => s + 1);
  };
  const next = () => {
    if (index + 1 >= quiz.questions.length) setFinished(true);
    else {
      setIndex(index + 1);
      setChoice(null);
      setSubmitted(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-4 rounded-lg border border-line-200 bg-white p-4 md:p-6"
    >
      <div className="flex flex-wrap items-baseline gap-x-4">
        {quiz.dataOrigin ? <DataOriginBadge origin={quiz.dataOrigin} /> : null}
        <span className="text-xs text-muted-600">
          Question {index + 1} of {quiz.questions.length}
        </span>
      </div>
      <fieldset className="flex flex-col gap-3">
        <legend>
          <h2
            ref={headingRef}
            tabIndex={-1}
            className="font-serif text-xl font-semibold text-navy-900 focus:outline-none"
          >
            {question.prompt}
          </h2>
        </legend>
        {question.options.map((o) => (
          <label
            key={o.id}
            className="flex cursor-pointer items-center gap-3 rounded-md border border-line-200 p-3 text-ink-900 has-checked:border-navy-900 has-checked:ring-1 has-checked:ring-navy-900"
          >
            <input
              type="radio"
              name={`q-${question.id}`}
              value={o.id}
              checked={choice === o.id}
              disabled={submitted}
              onChange={() => setChoice(o.id)}
              className="size-4 accent-navy-900"
            />
            {o.text}
          </label>
        ))}
      </fieldset>

      {submitted ? (
        <div
          role="status"
          className={`border-l-2 bg-white py-1 pl-4 ${correct ? 'border-ok-700' : 'border-danger-700'}`}
        >
          <p
            className={`flex items-center gap-2 font-semibold ${correct ? 'text-ok-700' : 'text-danger-700'}`}
          >
            {correct ? (
              <CheckCircle2 className="size-4" aria-hidden />
            ) : (
              <XCircle className="size-4" aria-hidden />
            )}
            {correct ? 'Correct' : 'Not quite'}
          </p>
          <p className="mt-1 text-ink-900">{question.explanation}</p>
          <div className="mt-2 flex flex-wrap gap-3">
            {renderSources
              ? renderSources(question)
              : (question.citationIds ?? []).map((pid) => {
                  const citation = citationForPassage(pid);
                  return citation ? <CitationLink key={pid} citation={citation} /> : null;
                })}
          </div>
        </div>
      ) : null}

      <div className="flex gap-2">
        {!submitted ? (
          <Button type="submit" disabled={!choice}>
            Check answer
          </Button>
        ) : (
          <Button type="button" onClick={next}>
            {index + 1 >= quiz.questions.length ? 'See result' : 'Next question'}
          </Button>
        )}
      </div>
    </form>
  );
}
